import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Trash2, Send, MapPin, Phone, Star, ArrowLeft } from 'lucide-react';
import Navigation from './Navigation';
import useShortlist from '../hooks/useShortlist';
import { providers as providersApi } from '../services/api';

function Shortlist() {
  const navigate = useNavigate();
  const { shortlist, remove, clear } = useShortlist();
  const [providerData, setProviderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [formData, setFormData] = useState({
    couple_name: '',
    couple_email: '',
    couple_phone: '',
    wedding_date: '',
    venue_location: '',
    guest_count: '',
    budget_range: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProviders = async () => {
      if (shortlist.length === 0) {
        setProviderData([]);
        setLoading(false);
        return;
      }

      try {
        const results = await Promise.all(
          shortlist.map(id =>
            providersApi.getById(id).then(res => res.data).catch(() => null)
          )
        );
        setProviderData(results.filter(Boolean));
      } catch (err) {
        console.error('Failed to load shortlisted providers:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProviders();
  }, [shortlist]);

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `https://api.bridalconnect.co.za${url}`;
  };

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Send individual enquiries to each shortlisted provider
      const results = await Promise.all(
        shortlist.map(providerId =>
          fetch('https://api.bridalconnect.co.za/api/enquiries/direct', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              provider_id: providerId,
              ...formData
            })
          }).then(res => res.json()).catch(() => ({ error: true }))
        )
      );

      const successCount = results.filter(r => !r.error).length;
      setSuccess(`Enquiry sent to ${successCount} of ${shortlist.length} providers!`);
      setShowEnquiryForm(false);
    } catch (err) {
      setError('Failed to send enquiries. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to directory
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Heart className="w-6 h-6 text-pink-500 fill-pink-500" />
              My Shortlist
            </h1>
            <p className="text-gray-600 mt-1">
              {shortlist.length === 0
                ? 'No providers shortlisted yet. Browse the directory and tap the heart icon to add.'
                : `${shortlist.length} provider${shortlist.length !== 1 ? 's' : ''} shortlisted`}
            </p>
          </div>

          {shortlist.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowEnquiryForm(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Contact All
              </button>
              <button
                onClick={() => { if (window.confirm('Clear entire shortlist?')) clear(); }}
                className="text-gray-400 hover:text-red-500 px-3 py-2 rounded-lg transition-colors"
                title="Clear shortlist"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Enquiry Form Modal */}
        {showEnquiryForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Send Enquiry to {shortlist.length} Providers</h2>
              <p className="text-gray-500 text-sm mb-4">Fill in your details once and we'll send it to all your shortlisted providers.</p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">{error}</div>
              )}

              <form onSubmit={handleSubmitEnquiry} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <input type="text" required value={formData.couple_name} onChange={e => setFormData({...formData, couple_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" required value={formData.couple_email} onChange={e => setFormData({...formData, couple_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={formData.couple_phone} onChange={e => setFormData({...formData, couple_phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Wedding Date</label>
                    <input type="date" value={formData.wedding_date} onChange={e => setFormData({...formData, wedding_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guest Count</label>
                    <input type="number" value={formData.guest_count} onChange={e => setFormData({...formData, guest_count: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Venue / Location</label>
                  <input type="text" value={formData.venue_location} onChange={e => setFormData({...formData, venue_location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea rows={3} value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Tell providers about your wedding plans..." />
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 transition-all">
                    {submitting ? 'Sending...' : `Send to ${shortlist.length} Providers`}
                  </button>
                  <button type="button" onClick={() => setShowEnquiryForm(false)}
                    className="px-4 py-2.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Provider List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {providerData.map(provider => (
              <div key={provider.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
                {/* Thumbnail */}
                {(provider.banner_url || provider.logo_url || provider.thumbnail_url) ? (
                  <img
                    src={getImageUrl(provider.banner_url || provider.logo_url || provider.thumbnail_url)}
                    alt={provider.business_name}
                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0 cursor-pointer"
                    onClick={() => navigate(`/provider/${provider.id}`)}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex'); }}
                  />
                ) : null}
                <div
                  className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-400 to-pink-400 flex-shrink-0 items-center justify-center cursor-pointer"
                  style={{ display: (provider.banner_url || provider.logo_url || provider.thumbnail_url) ? 'none' : 'flex' }}
                  onClick={() => navigate(`/provider/${provider.id}`)}
                />

                {/* Info */}
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/provider/${provider.id}`)}>
                  <h3 className="font-semibold text-gray-900 truncate">{provider.business_name}</h3>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
                    {provider.category_name && (
                      <span className="text-purple-600 font-medium">{provider.category_name}</span>
                    )}
                    {(provider.city || provider.province) && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[provider.city, provider.province].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {(provider.google_rating || provider.facebook_rating) && (
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        {(provider.google_rating || provider.facebook_rating).toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => remove(provider.id)}
                  className="p-2 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                  title="Remove from shortlist"
                >
                  <Heart className="w-5 h-5 fill-pink-500 text-pink-500" />
                </button>
              </div>
            ))}

            {shortlist.length === 0 && (
              <div className="text-center py-16">
                <Heart className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-500 mb-2">Your shortlist is empty</h3>
                <p className="text-gray-400 mb-6">Browse providers and tap the heart icon to save your favourites.</p>
                <Link to="/" className="text-purple-600 hover:text-purple-800 font-medium">
                  Browse providers &rarr;
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Shortlist;
